import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, Grid, Container, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, FormHelperText, Autocomplete, CircularProgress } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Page from 'src/components/Page';
import Loader from 'src/components/Loader';
import communication from 'src/constants/Communication';
import { debounce } from 'lodash';
import { authenticatedInstance } from 'src/utils/axiosInstance';

export default function CertificateCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [certificateTemplates, setCertificateTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [fonts, setFonts] = useState([]);
  const [loadingFonts, setLoadingFonts] = useState(false);

  const validationSchema = Yup.object({
    registrationId: Yup.string().required('Registration ID is required'),
    alternateName: Yup.string().max(25, 'Maximum 25 characters'),
    alternateEmail: Yup.string().email('Enter a valid email'),
    course: Yup.string().required('Course is required'),
    duration: Yup.number().required('Duration is required'),
    expiryDate: Yup.date(),
    templateUsed: Yup.string().required('Template is required'),
    font: Yup.string().optional(),
  });

  const formik = useFormik({
    initialValues: {
      registrationId: '',
      alternateName: '',
      alternateEmail: '',
      course: '',
      duration: '',
      expiryDate: null,
      templateUsed: '',
      font: '',
      metadata: {},
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await authenticatedInstance.post(communication.certificates, {
          registrationId: values.registrationId,
          alternateName: values.alternateName,
          alternateEmail: values.alternateEmail,
          course: values.course,
          expiryDate: values.expiryDate,
          templateUsed: values.templateUsed,
          font: values.font,
          metadata: {
            duration: values.duration,
          }
        });
        navigate('/certificates');
      } catch (err) {
        setError('Error creating certificate. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
  });

  // Fetch certificate templates on component mount
  useEffect(() => {
    const fetchCertificateTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const response = await authenticatedInstance.get(communication.getAllCertificatesTemplates);
        if (response.data && response.data.templates) {
          setCertificateTemplates(response.data.templates);
          // Set the first template as default if available
          if (response.data.templates.length > 0) {
            formik.setFieldValue('templateUsed', response.data.templates[0].type);
          }
        }
      } catch (err) {
        console.error('Error fetching certificate templates:', err);
        setError('Failed to load certificate templates.');
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchCertificateTemplates();
  }, []);

  // Fetch available fonts
  useEffect(() => {
    const fetchFonts = async () => {
      setLoadingFonts(true);
      try {
        const response = await authenticatedInstance.get(communication.getAllFonts);
        if (response.data && response.data.fonts) {
          setFonts(response.data.fonts);
          // Set the first font as default if available
          if (response.data.fonts.length > 0) {
            formik.setFieldValue('fontFamily', response.data.fonts[0].name);
          }
        }
      } catch (err) {
        console.error('Error fetching fonts:', err);
        setError('Failed to load available fonts.');
      } finally {
        setLoadingFonts(false);
      }
    };

    fetchFonts();
  }, []);

  // Debounced function to search for users
  const fetchUsers = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setUserOptions([]);
        setUserSearchLoading(false);
        return;
      }

      setUserSearchLoading(true);
      try {
        // Check if searchTerm has email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isEmail = emailRegex.test(searchTerm);
        
        // Use email or firstName based on search term format
        const searchCriteria = isEmail 
          ? { email: searchTerm }
          : { firstName: searchTerm };

        // Replace with the actual API endpoint for searching users
        const response = await authenticatedInstance.get(communication.getRegisterations, {
          params: { limit: 10, page: 1, searchCriteria }
        });
        const users = response.data.results;
        setUserOptions(users);
      } catch (err) {
        console.error('Error searching users:', err);
        setUserOptions([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 500), // 500ms debounce time
    []
  );

  // Handle user search input change
  const handleUserSearchChange = (event, value) => {
    // Skip searching if selectedUser exists and the input is part of the selected user's name
    if (selectedUser && (value?.includes(selectedUser.firstName) || value === '')) {
      return;
    }
    
    if (typeof value === 'string') {
      fetchUsers(value);
    }
  };

  // Handle user selection
  const handleUserChange = (event, newValue) => {
    setSelectedUser(newValue);
    
    // Clear options once a user is selected to prevent further API calls
    if (newValue) {
      setUserOptions([]);
      formik.setFieldValue('registrationId', newValue.id || '');
    } else {
      formik.setFieldValue('registrationId', '');
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Page title="Create Certificate">
      <Container>
        <Typography variant="h4" sx={{ mb: 5 }}>
          Create New Certificate
        </Typography>

        <Card>
          <CardHeader title="Certificate Information" />
          <CardContent>
            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    id="user-search"
                    options={userOptions}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : `${option.firstName} (${option.email})`
                    }
                    onInputChange={handleUserSearchChange}
                    onChange={handleUserChange}
                    filterOptions={(x) => x}
                    freeSolo
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search User"
                        fullWidth
                        error={formik.touched.registrationId && Boolean(formik.errors.registrationId)}
                        helperText={formik.touched.registrationId && formik.errors.registrationId}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  {selectedUser && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      Selected: {selectedUser.firstName} (ID: {selectedUser.id})
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Alternate Name (Optional)"
                    name="alternateName"
                    value={formik.values.alternateName}
                    onChange={formik.handleChange}
                    error={formik.touched.alternateName && Boolean(formik.errors.alternateName)}
                    helperText={formik.touched.alternateName && formik.errors.alternateName}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Alternate Email (Optional)"
                    name="alternateEmail"
                    value={formik.values.alternateEmail}
                    onChange={formik.handleChange}
                    error={formik.touched.alternateEmail && Boolean(formik.errors.alternateEmail)}
                    helperText={formik.touched.alternateEmail && formik.errors.alternateEmail}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Course"
                    name="course"
                    value={formik.values.course}
                    onChange={formik.handleChange}
                    error={formik.touched.course && Boolean(formik.errors.course)}
                    helperText={formik.touched.course && formik.errors.course}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Duration (in hours)"
                    name="duration"
                    value={formik.values.duration}
                    onChange={formik.handleChange}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Expiry Date"
                      value={formik.values.expiryDate}
                      onChange={(value) => {
                        formik.setFieldValue('expiryDate', value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          name="expiryDate"
                          error={formik.touched.expiryDate && Boolean(formik.errors.expiryDate)}
                          helperText={formik.touched.expiryDate && formik.errors.expiryDate}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={formik.touched.templateUsed && Boolean(formik.errors.templateUsed)}
                  >
                    <InputLabel>Certificate Template</InputLabel>
                    <Select
                      name="templateUsed"
                      value={formik.values.templateUsed}
                      label="Certificate Template"
                      onChange={formik.handleChange}
                      disabled={loadingTemplates}
                    >
                      {loadingTemplates ? (
                        <MenuItem value="">
                          <CircularProgress size={20} /> Loading templates...
                        </MenuItem>
                      ) : (
                        certificateTemplates.map((template) => (
                          <MenuItem key={template.type} value={template.type}>
                            {template.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {formik.touched.templateUsed && formik.errors.templateUsed && (
                      <FormHelperText>{formik.errors.templateUsed}</FormHelperText>
                    )}
                    {certificateTemplates.length > 0 && formik.values.templateUsed && (
                      <Typography variant="caption" sx={{ mt: 1 }}>
                        {certificateTemplates.find(t => t.type === formik.values.templateUsed)?.description}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={formik.touched.font && Boolean(formik.errors.font)}
                  >
                    <InputLabel>Font</InputLabel>
                    <Select
                      name="font"
                      value={formik.values.font}
                      label="Font"
                      onChange={formik.handleChange}
                      disabled={loadingFonts}
                    >
                      {loadingFonts ? (
                        <MenuItem value="">
                          <CircularProgress size={20} /> Loading fonts...
                        </MenuItem>
                      ) : (
                        fonts.map((font) => (
                          <MenuItem key={font} value={font}>
                            {font}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {formik.touched.font && formik.errors.font && (
                      <FormHelperText>{formik.errors.font}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                  >
                    Create Certificate
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="large"
                    onClick={() => navigate('/certificates')}
                    sx={{ ml: 2 }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
} 